<template>
  <div v-if="deviceMetaData">
    <div class="mb-1">
      <span class="subtitle2" data-testid="ticket-preview-title"> {{ usersTitle }}: </span>
      <span class="body2" data-testid="ticket-preview-users" v-html="users"></span>
    </div>

    <div v-if="showDetectionRange" class="mb-1">
      <span class="subtitle2">{{ $t("ticketDetails.firstDetection") }}: </span>
      <span class="body2" data-testid="ticket-preview-first-detection">
        {{ getFormattedDateTime(ticketDetails.startTime) }}
      </span>
    </div>

    <div class="mb-1">
      <span class="subtitle2">{{ ticketEndTimeLabel }}: </span>
      <span class="body2" data-testid="ticket-preview-last-seen">
        {{ getFormattedDateTime(ticketDetails.endTime) }}
      </span>
    </div>

    <div class="mb-1 d-flex align-center">
      <div class="subtitle2 mr-1">{{ $t("ticketDetails.enrollmentCode") }}:</div>
      <div v-if="hideLink" class="body2" data-testid="ticket-preview-enrollment-code">
        {{ deviceMetaData.enrollmentCode }}
      </div>
      <div
        v-else
        class="body2 coro-link"
        data-testid="ticket-preview-enrollment-code"
        @click="handleDeviceLinkClick()"
      >
        {{ deviceMetaData.enrollmentCode }}
      </div>
    </div>

    <div class="mb-1 d-flex align-center">
      <div class="subtitle2 mr-1">{{ $t("deviceDetails.device.hostName") }}:</div>
      <div class="body2" data-testid="ticket-preview-hostname">
        {{ deviceMetaData.hostname }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useTicketPreviewGeneralInfo } from "@/composables/useTicketPreviewGeneralInfo";
import { useI18n } from "vue-i18n";
import { Service } from "@/constants/cloud-apps";
import { TicketsModal, TicketTrigger } from "@/constants/tickets";
import { ProxyDirection } from "@/constants/email-proxy";
import type { TicketDetails } from "@/_store/tickets/tickets.module.ts";

export default defineComponent({
  props: {
    ticketDetails: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
    hideLink: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["device-link-click"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const deviceMetaData = computed(() => props.ticketDetails?.sections?.deviceMetaData);

    const { getFormattedDateTime, addUsersNotAllowed, usersToAdd, addUsers, openListModal } =
      useTicketPreviewGeneralInfo(props.ticketDetails);

    const users = computed(() => {
      const lastLoggedInUsers = props.ticketDetails?.lastLoggedInUsers ?? [];
      return lastLoggedInUsers.length
        ? lastLoggedInUsers.join(", <br>")
        : props.ticketDetails.sections.triggeredUser;
    });

    const usersTitle = computed(() => {
      const lastLoggedInUsers = props.ticketDetails?.lastLoggedInUsers?.length;
      return lastLoggedInUsers
        ? i18n.t("ticketDetails.lastUsersOnDevice")
        : i18n.t("ticketDetails.users");
    });

    const serviceNameTitle = computed(() => {
      const serviceName = i18n.t(`services.${props.ticketDetails.sections.serviceName}`);
      if (props.ticketDetails.sections.serviceName === Service.PROXY) {
        const direction =
          props.ticketDetails.sections.emailMetaData?.emailProxyDirection === ProxyDirection.INBOUND
            ? i18n.t("emailProxy.directions.inbound")
            : i18n.t("emailProxy.directions.outbound");

        return `${direction} ${serviceName}`;
      }
      return serviceName;
    });

    const showDetectionRange = computed(
      () => props.ticketDetails.eventTrigger === TicketTrigger.DLP_DEVICE_SENSITIVE_DATA
    );

    const ticketEndTimeLabel = computed(() => {
      if (showDetectionRange.value) {
        return i18n.t("ticketDetails.lastDetection");
      }

      const endpointDlpTriggers = [
        TicketTrigger.DEVICE_DLP_PERSONAL,
        TicketTrigger.DEVICE_DLP_HEALTH,
        TicketTrigger.DEVICE_DLP_CREDIT_CARD,
        TicketTrigger.DEVICE_DLP_NON_PUBLIC,
      ];
      return endpointDlpTriggers.includes(props.ticketDetails.eventTrigger)
        ? i18n.t("ticketDetails.detectedOn")
        : i18n.t("ticketDetails.lastSeen");
    });

    const handleDeviceLinkClick = () => {
      emit("device-link-click");
    };

    return {
      users,
      getFormattedDateTime,
      addUsersNotAllowed,
      usersToAdd,
      addUsers,
      deviceMetaData,
      serviceNameTitle,
      openListModal,
      TicketsModal,
      Service,
      handleDeviceLinkClick,
      usersTitle,
      ticketEndTimeLabel,
      showDetectionRange,
    };
  },
});
</script>

<style lang="scss" scoped>
.service-icon {
  width: 40px;
  height: 40px;
}
</style>
